import type { ActionArgs, LoaderArgs, MetaFunction} from "@remix-run/node";
import { json, redirect } from "@remix-run/node";
import clsx from "clsx";
import { Form, useActionData, useLoaderData, useTransition } from "@remix-run/react";
import { createOGP } from "@/utilities/ogp";
import common from "@/data/common.json";
import { PageContainer } from "@/components/PageContainer";
import { ArticleLayout } from "@/components/ArticleLayout";
import { DisplayText } from "@/components/DisplayText";
import { Loading } from "@/components/Loader";
import { inquirySchema } from "@/lib/schema";
import { sanitizeText } from "@/lib/format";
import { sendInquiry } from "@/lib/email.server";
import { FieldsError } from "@/components/Fields/Error";
import { errorTextID, useID } from "@/components/utils";
import { FieldsInput } from "@/components/Fields/Input";
import { FieldsTextarea } from "@/components/Fields/Textarea";
import { createCSRFToken, verifyCSRFToken } from "@/lib/csrf.server";
import { commitSession, getSession } from "@/lib/sessions.server";

export const meta: MetaFunction = () => {
  return createOGP({ path: `${common.meta.url}/contact/inquiry`, pageName: "施設へのお問い合わせ" });
};

export async function loader({ request }: LoaderArgs) {
  const session = await getSession(request);
  const csrf = createCSRFToken();
  session.set("_csrf", csrf);
  const headers = await commitSession(session);

  return json({ csrf }, { headers });
}

export async function action({ request }: ActionArgs) {
  const session = await getSession(request);
  const formData = await request.formData();

  const verified = verifyCSRFToken(session, formData);
  if (!verified) {
    return json(
      {
        ok: false,
        errors: {
          formErrors: ["無効なリクエストが送信されました。"],
          fieldErrors: null,
        },
      },
      { status: 400 }
    );
  }

  const result = await inquirySchema.safeParseAsync({
    email: formData.get(`inquiry[email]`),
    body: formData.get(`inquiry[body]`),
  });
  if (!result.success) {
    return json({ ok: false, errors: result.error.flatten() }, { status: 400 });
  }

  const bodyText = sanitizeText(result.data.body);
  const sendResult = await sendInquiry({
    ...result.data,
    body: bodyText,
  });

  if (!sendResult) {
    return json(
      {
        ok: false,
        errors: {
          formErrors: ["送信エラーが発生しました。ウェブサイト管理者へお問い合わせ下さい。"],
          fieldErrors: null,
        },
      },
      { status: 500 }
    );
  }

  session.flash("_inquiry_sent", true);

  return redirect("/contact/inquiry/success", { headers: await commitSession(session) });
}

export default function Inquiry() {
  const { csrf } = useLoaderData<typeof loader>();
  const actionData = useActionData<typeof action>();
  const transition = useTransition();
  const formID = useID();

  const emailID = useID();
  const bodyID = useID();

  return (
    <PageContainer>
      <ArticleLayout
        size="small"
        paths={[
          { name: "ホーム", url: "/" },
          { name: "お問い合わせ", url: "/contact" },
          { name: "施設へのお問い合わせ", url: "/contact/inquiry" },
        ]}
      >
        <div className={clsx("flex", "flex-col", "space-y-8")}>
          <DisplayText element="h1" size="extraLarge">
            施設へのお問い合わせ
          </DisplayText>

          <div className="relative">
            <section className={clsx("border-b", "border-b-gray-200", "pb-8", "mb-8")}>
              <p>
                施設へお問い合わせをご希望の方はこちらよりご連絡ください。
                <br />
                ご入力いただいたメールアドレス宛に運営よりご返信致します。
              </p>
              <p className="mt-2">
                なお、お問い合わせには順次ご対応させていただいておりますが、ご対応まで時間や日数のかかる場合がございます。
                <br />
                お急ぎの場合は、025-278-8296（万代島多目的広場事務室 受付時間:
                9時〜21時）までお電話をいただきますようお願い致します。
              </p>
              <p className="mt-2">
                また、内容によってはご回答差し上げられない場合もございます。あらかじめご了承ください。
              </p>
            </section>

            <Form id={formID} method="post" aria-describedby={errorTextID(formID)}>
              <input type="hidden" name="_csrf" value={csrf} />
              <div className={clsx("flex", "flex-col", "space-y-4")}>
                {actionData?.errors?.formErrors && actionData.errors.formErrors.length > 0 && (
                  <FieldsError id={errorTextID(formID)}>{actionData.errors.formErrors}</FieldsError>
                )}
                <div className={clsx("flex", "flex-col", "space-y-8")}>
                  <FieldsInput
                    id={emailID}
                    name={`inquiry[email]`}
                    type="text"
                    labelText="Eメールアドレス"
                    errorText={actionData?.errors?.fieldErrors?.email}
                    disabled={transition.state === "submitting"}
                  />
                  <FieldsTextarea
                    id={bodyID}
                    name={`inquiry[body]`}
                    rows={4}
                    maxLength={1000}
                    labelText="お問い合わせ内容"
                    errorText={actionData?.errors?.fieldErrors?.body}
                    disabled={transition.state === "submitting"}
                  />
                  <div className={clsx("flex", "justify-end")}>
                    <button
                      type="submit"
                      className="button button--primary"
                      disabled={transition.state === "submitting"}
                    >
                      <span className="button__backdrop" />
                      <span className="button__content">送信</span>
                    </button>
                  </div>
                </div>
              </div>
            </Form>

            {transition.state === "submitting" && <Loading.Absolute />}
          </div>
        </div>
      </ArticleLayout>
    </PageContainer>
  );
}
